import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabase/supabaseClient";
import { trpc } from "@/lib/trpc/client";
import { trpcVanilla } from "@/lib/trpc/vanilla";
import {
  patchKeyedListCache,
  prependToList,
  replaceInList,
} from "@trainrr/api-client";
import { toast } from "sonner";
import {
  VAULT_MAX_FILE_SIZE_BYTES,
  type VaultUploadContext,
  type VaultUploadItem,
} from "./types";

const MAX_CONCURRENT_UPLOADS = 3;
const READY_UPLOAD_RETENTION_MS = 60_000;
const MAX_UPLOAD_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 500;

type UploadManagerContextValue = {
  uploads: VaultUploadItem[];
  activeUploadCount: number;
  enqueueVaultFiles: (files: File[] | FileList, context: VaultUploadContext) => string[];
  enqueueRetryForExistingFile: (
    file: File,
    context: VaultUploadContext,
    fileId: string,
  ) => string | null;
  retryUpload: (uploadId: string) => void;
  dismissUpload: (uploadId: string) => void;
  dismissUploadsByFileIds: (fileIds: string[]) => void;
};

const UploadManagerContext = createContext<UploadManagerContextValue | undefined>(
  undefined,
);

const isUploadActive = (status: VaultUploadItem["status"]) =>
  status === "queued" ||
  status === "initializing" ||
  status === "uploading" ||
  status === "finalizing";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const formatError = (fallbackMessage: string, error: unknown) => {
  if (typeof error === "string" && error.trim().length > 0) {
    return `${fallbackMessage}: ${error}`;
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim().length > 0) {
      return `${fallbackMessage}: ${message}`;
    }
  }
  return fallbackMessage;
};

const isTooLargeError = (error: unknown) => {
  if (!error || typeof error !== "object") return false;
  const maybeError = error as {
    message?: unknown;
    details?: unknown;
    code?: unknown;
    status?: unknown;
  };
  const message = typeof maybeError.message === "string"
    ? maybeError.message.toLowerCase()
    : "";
  const details = typeof maybeError.details === "string"
    ? maybeError.details.toLowerCase()
    : "";
  const code = typeof maybeError.code === "string"
    ? maybeError.code.toLowerCase()
    : "";
  const status = typeof maybeError.status === "number" ? maybeError.status : null;

  return (
    status === 413 ||
    message.includes("too large") ||
    message.includes("payload too large") ||
    message.includes("maximum size") ||
    message.includes("max file size") ||
    message.includes("50mb") ||
    details.includes("too large") ||
    details.includes("maximum size") ||
    details.includes("50mb") ||
    code.includes("payload_too_large")
  );
};

const getErrorLogContext = (error: unknown): Record<string, unknown> => {
  if (!error || typeof error !== "object") {
    return {};
  }

  const maybeError = error as {
    message?: unknown;
    name?: unknown;
    status?: unknown;
    code?: unknown;
    details?: unknown;
    hint?: unknown;
    context?: unknown;
  };

  return {
    name: typeof maybeError.name === "string" ? maybeError.name : undefined,
    message: typeof maybeError.message === "string" ? maybeError.message : undefined,
    status: typeof maybeError.status === "number" ? maybeError.status : undefined,
    code: typeof maybeError.code === "string" ? maybeError.code : undefined,
    details: typeof maybeError.details === "string" ? maybeError.details : undefined,
    hint: typeof maybeError.hint === "string" ? maybeError.hint : undefined,
    hasContext: Boolean(maybeError.context),
  };
};

export function UploadManagerProvider({ children }: { children: React.ReactNode }) {
  const utils = trpc.useUtils();
  const [uploads, setUploads] = useState<VaultUploadItem[]>([]);
  const inFlightUploadIdsRef = useRef<Set<string>>(new Set());
  const uploadsRef = useRef<VaultUploadItem[]>(uploads);
  const handledCompletionToastsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  const updateUpload = useCallback(
    (
      uploadId: string,
      updater: (item: VaultUploadItem) => Partial<VaultUploadItem>,
    ) => {
      setUploads((current) =>
        current.map((item) =>
          item.id === uploadId
            ? {
                ...item,
                ...updater(item),
                updatedAt: new Date().toISOString(),
              }
            : item,
        ),
      );
    },
    [],
  );

  const markUploadFailed = useCallback(
    (
      uploadId: string,
      errorMessage: string,
      errorCode: VaultUploadItem["errorCode"] = null,
    ) => {
      updateUpload(uploadId, () => ({
        status: "failed",
        errorMessage,
        errorCode,
        completedAt: new Date().toISOString(),
      }));
    },
    [updateUpload],
  );

  const runUpload = useCallback(
    async (uploadId: string) => {
      let lastErrorMessage = "Upload failed";
      let lastErrorCode: VaultUploadItem["errorCode"] = null;

      for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
        const upload = uploadsRef.current.find((item) => item.id === uploadId);
        if (!upload) return;
        const effectiveSize = Math.max(upload.sizeBytes, upload.file.size);
        if (effectiveSize > VAULT_MAX_FILE_SIZE_BYTES) {
          markUploadFailed(
            uploadId,
            "File is too large. Max upload size is 50MB.",
            "file_too_large",
          );
          return;
        }

        updateUpload(uploadId, (item) => ({
          status: "initializing",
          errorMessage: null,
          errorCode: null,
          completedAt: null,
          fileId: item.fileId,
          storageBucket: item.storageBucket,
          storageObjectPath: item.storageObjectPath,
        }));

        const { data: initData, error: initError } = await (async () => {
          try {
            const existingFileId =
              uploadsRef.current.find((item) => item.id === uploadId)?.fileId ??
              null;
            const created = existingFileId
              ? await trpcVanilla.vault.retryUpload.mutate({
                  fileId: existingFileId,
                  fileName: upload.fileName,
                  mimeType: upload.mimeType,
                  sizeBytes: upload.sizeBytes,
                })
              : await trpcVanilla.vault.createUpload.mutate({
                  vaultId: upload.context.vaultId,
                  folderId: upload.context.folderId,
                  fileName: upload.fileName,
                  mimeType: upload.mimeType,
                  sizeBytes: upload.sizeBytes,
                });
            patchKeyedListCache(
              utils.vault.listFiles,
              {
                vaultId: upload.context.vaultId,
                folderId: upload.context.folderId,
              },
              (old) => prependToList(old, created.file),
            );
            patchKeyedListCache(
              utils.vault.listSearchFiles,
              { vaultId: upload.context.vaultId },
              (old) => prependToList(old, created.file),
            );
            return { data: created, error: null };
          } catch (error) {
            return { data: null, error };
          }
        })();

        if (initError || !initData) {
          console.error("Failed to init upload", {
            uploadId,
            attempt,
            error: initError,
            ...getErrorLogContext(initError),
          });
          lastErrorMessage = formatError("Failed to initialize upload", initError);
          lastErrorCode = null;
        } else if (!initData.token || !initData.signedUrl) {
          updateUpload(uploadId, () => ({
            fileId: initData.fileId,
            storageBucket: initData.bucket,
            storageObjectPath: initData.path,
          }));
          lastErrorMessage = "Failed to initialize upload";
          lastErrorCode = null;
        } else {
          updateUpload(uploadId, () => ({
            status: "uploading",
            fileId: initData.fileId,
            storageBucket: initData.bucket,
            storageObjectPath: initData.path,
          }));

          const { error: signedUploadError } = await supabase.storage
            .from(initData.bucket)
            .uploadToSignedUrl(initData.path, initData.token, upload.file);

          updateUpload(uploadId, () => ({
            status: "finalizing",
          }));

          const { error: finalizeError } = await (async () => {
            try {
              const finalized = await trpcVanilla.vault.finalizeUpload.mutate({
                fileId: initData.fileId,
              });
              patchKeyedListCache(
                utils.vault.listFiles,
                {
                  vaultId: upload.context.vaultId,
                  folderId: upload.context.folderId,
                },
                (old) => replaceInList(old, finalized),
              );
              patchKeyedListCache(
                utils.vault.listSearchFiles,
                { vaultId: upload.context.vaultId },
                (old) => replaceInList(old, finalized),
              );
              return { error: null };
            } catch (error) {
              return { error };
            }
          })();

          if (finalizeError) {
            console.error("Finalize upload failed", {
              uploadId,
              attempt,
              error: finalizeError,
              ...getErrorLogContext(finalizeError),
            });
            lastErrorMessage = formatError(
              "Upload finalization failed",
              finalizeError,
            );
            lastErrorCode = null;
          } else if (signedUploadError) {
            console.error("Signed upload failed", {
              uploadId,
              attempt,
              error: signedUploadError,
              ...getErrorLogContext(signedUploadError),
            });
            if (isTooLargeError(signedUploadError)) {
              lastErrorMessage = "File is too large. Max upload size is 50MB.";
              lastErrorCode = "file_too_large";
            } else {
              lastErrorMessage = formatError("Upload failed", signedUploadError);
              lastErrorCode = null;
            }
          } else {
            updateUpload(uploadId, () => ({
              status: "ready",
              errorMessage: null,
              completedAt: new Date().toISOString(),
            }));
            return;
          }
        }

        if (attempt < MAX_UPLOAD_ATTEMPTS) {
          updateUpload(uploadId, (item) => ({
            status: "queued",
            errorMessage: null,
            errorCode: null,
            fileId: item.fileId,
            storageBucket: item.storageBucket,
            storageObjectPath: item.storageObjectPath,
          }));
          await sleep(RETRY_BASE_DELAY_MS * attempt);
        }
      }

      markUploadFailed(uploadId, lastErrorMessage, lastErrorCode);
    },
    [markUploadFailed, updateUpload, utils],
  );

  const enqueueVaultFiles = useCallback(
    (files: File[] | FileList, context: VaultUploadContext) => {
      const selectedFiles = Array.from(files);
      if (selectedFiles.length === 0) return [];

      const nowIso = new Date().toISOString();
      const newUploads: VaultUploadItem[] = selectedFiles.map((file) => ({
        id: typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        context,
        file,
        fileName: file.name,
        mimeType: file.type || null,
        sizeBytes: file.size,
        fileId: null,
        storageBucket: null,
        storageObjectPath: null,
        errorMessage:
          file.size > VAULT_MAX_FILE_SIZE_BYTES
            ? "File is too large. Max upload size is 50MB."
            : null,
        errorCode: file.size > VAULT_MAX_FILE_SIZE_BYTES ? "file_too_large" : null,
        createdAt: nowIso,
        updatedAt: nowIso,
        completedAt: file.size > VAULT_MAX_FILE_SIZE_BYTES ? nowIso : null,
        attempt: 1,
        status: file.size > VAULT_MAX_FILE_SIZE_BYTES ? "failed" : "queued",
      }));

      setUploads((current) => [...current, ...newUploads]);
      return newUploads.map((item) => item.id);
    },
    [],
  );

  const retryUpload = useCallback((uploadId: string) => {
    setUploads((current) =>
      current.map((item) =>
        item.id === uploadId
          ? {
              ...item,
              status: "queued",
              errorMessage: null,
              errorCode: null,
              completedAt: null,
              updatedAt: new Date().toISOString(),
              attempt: item.attempt + 1,
            }
          : item,
      ),
    );
  }, []);

  const enqueueRetryForExistingFile = useCallback(
    (file: File, context: VaultUploadContext, fileId: string) => {
      const existing = uploadsRef.current.find((item) => item.fileId === fileId);
      if (existing) {
        setUploads((current) =>
          current.map((item) =>
            item.id === existing.id
              ? {
                  ...item,
                  context,
                  file,
                  fileName: file.name,
                  mimeType: file.type || null,
                  sizeBytes: file.size,
                  fileId,
                  status: file.size > VAULT_MAX_FILE_SIZE_BYTES ? "failed" : "queued",
                  errorMessage:
                    file.size > VAULT_MAX_FILE_SIZE_BYTES
                      ? "File is too large. Max upload size is 50MB."
                      : null,
                  errorCode:
                    file.size > VAULT_MAX_FILE_SIZE_BYTES ? "file_too_large" : null,
                  completedAt:
                    file.size > VAULT_MAX_FILE_SIZE_BYTES
                      ? new Date().toISOString()
                      : null,
                  updatedAt: new Date().toISOString(),
                  attempt: item.attempt + 1,
                }
              : item,
          ),
        );
        return existing.id;
      }

      const nowIso = new Date().toISOString();
      const tooLarge = file.size > VAULT_MAX_FILE_SIZE_BYTES;
      const newUpload: VaultUploadItem = {
        id:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        context,
        file,
        fileName: file.name,
        mimeType: file.type || null,
        sizeBytes: file.size,
        fileId,
        storageBucket: null,
        storageObjectPath: null,
        errorMessage: tooLarge
          ? "File is too large. Max upload size is 50MB."
          : null,
        errorCode: tooLarge ? "file_too_large" : null,
        createdAt: nowIso,
        updatedAt: nowIso,
        completedAt: tooLarge ? nowIso : null,
        attempt: 1,
        status: tooLarge ? "failed" : "queued",
      };
      setUploads((current) => [...current, newUpload]);
      return newUpload.id;
    },
    [],
  );

  const dismissUpload = useCallback((uploadId: string) => {
    setUploads((current) => current.filter((item) => item.id !== uploadId));
  }, []);

  const dismissUploadsByFileIds = useCallback((fileIds: string[]) => {
    if (fileIds.length === 0) return;
    const fileIdSet = new Set(fileIds);
    setUploads((current) =>
      current.filter((item) => !item.fileId || !fileIdSet.has(item.fileId)),
    );
  }, []);

  const activeUploadCount = useMemo(
    () => uploads.filter((item) => isUploadActive(item.status)).length,
    [uploads],
  );

  useEffect(() => {
    const availableSlots = MAX_CONCURRENT_UPLOADS - inFlightUploadIdsRef.current.size;
    if (availableSlots <= 0) return;

    const queuedUploads = uploads
      .filter((item) => item.status === "queued")
      .filter((item) => !inFlightUploadIdsRef.current.has(item.id))
      .slice(0, availableSlots);

    queuedUploads.forEach((item) => {
      inFlightUploadIdsRef.current.add(item.id);
      void runUpload(item.id).finally(() => {
        inFlightUploadIdsRef.current.delete(item.id);
      });
    });
  }, [runUpload, uploads]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const now = Date.now();
      setUploads((current) =>
        current.filter((item) => {
          if (item.status !== "ready" || !item.completedAt) return true;
          const completedAt = new Date(item.completedAt).getTime();
          if (!Number.isFinite(completedAt)) return true;
          return now - completedAt < READY_UPLOAD_RETENTION_MS;
        }),
      );
    }, 10_000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const completedUploads = uploads.filter(
      (upload) =>
        upload.completedAt && (upload.status === "ready" || upload.status === "failed"),
    );

    const unhandledCompletions = completedUploads.filter((upload) => {
      const completionKey = `${upload.id}:${upload.completedAt ?? ""}`;
      return !handledCompletionToastsRef.current.has(completionKey);
    });
    if (unhandledCompletions.length === 0) return;

    unhandledCompletions.forEach((upload) => {
      const completionKey = `${upload.id}:${upload.completedAt ?? ""}`;
      handledCompletionToastsRef.current.add(completionKey);
    });

    const succeeded = unhandledCompletions.filter(
      (upload) => upload.status === "ready",
    );
    const failed = unhandledCompletions.filter(
      (upload) => upload.status === "failed",
    );

    if (succeeded.length === 1) {
      toast.success(`Uploaded "${succeeded[0]?.fileName ?? "file"}"`);
    } else if (succeeded.length > 1) {
      toast.success(`${succeeded.length} files uploaded`);
    }

    if (failed.length === 1) {
      const failedUpload = failed[0];
      if (failedUpload?.errorCode === "file_too_large") {
        toast.error(`"${failedUpload.fileName}" is too large. Max upload size is 50MB.`);
        return;
      }
      toast.error(
        failedUpload?.errorMessage ??
          `Upload failed for "${failedUpload?.fileName ?? "file"}"`,
      );
    } else if (failed.length > 1) {
      const tooLargeCount = failed.filter(
        (upload) => upload.errorCode === "file_too_large",
      ).length;
      if (tooLargeCount === failed.length) {
        toast.error(
          tooLargeCount === 1
            ? "1 file is too large (max 50MB)."
            : `${tooLargeCount} files are too large (max 50MB).`,
        );
        return;
      }
      toast.error(`${failed.length} files failed to upload`);
    }
  }, [uploads]);

  useEffect(() => {
    const activeCompletionKeys = new Set(
      uploads
        .filter((upload) => upload.completedAt)
        .map((upload) => `${upload.id}:${upload.completedAt ?? ""}`),
    );
    handledCompletionToastsRef.current.forEach((completionKey) => {
      if (!activeCompletionKeys.has(completionKey)) {
        handledCompletionToastsRef.current.delete(completionKey);
      }
    });
  }, [uploads]);

  const value = useMemo<UploadManagerContextValue>(
    () => ({
      uploads,
      activeUploadCount,
      enqueueVaultFiles,
      enqueueRetryForExistingFile,
      retryUpload,
      dismissUpload,
      dismissUploadsByFileIds,
    }),
    [
      activeUploadCount,
      dismissUpload,
      dismissUploadsByFileIds,
      enqueueRetryForExistingFile,
      enqueueVaultFiles,
      retryUpload,
      uploads,
    ],
  );

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
}

export const useUploadManager = () => {
  const context = useContext(UploadManagerContext);
  if (!context) {
    throw new Error("useUploadManager must be used within UploadManagerProvider.");
  }
  return context;
};
